import * as React from "react";
import classnames from "classnames";
import { debounce } from "lodash";

import { TRUSEventShape } from "web_modules/Events/types";

import Description from "./Description";
import Title from "./Title";
import {
  formatTime,
  isArchivedKind,
  isCloneKind,
  isConditionKind,
  isHurdleNoteKind,
  isLobLetterKind,
  isSSNKind,
  isFieldUpdateKind,
  kindToIcon,
} from "../shared";

const FontAwesome = require("react-fontawesome");

const styles = require("../styles.module.scss");

const eventDoesNotHaveExpandedContent = (eventKind: string): boolean =>
  isArchivedKind(eventKind) ||
  isCloneKind(eventKind) ||
  isConditionKind(eventKind) ||
  isHurdleNoteKind(eventKind) ||
  isLobLetterKind(eventKind) ||
  isSSNKind(eventKind) ||
  isFieldUpdateKind(eventKind);

const eventAuthor = (e: TRUSEventShape): string => {
  let author;
  if (!e.author_email || e.author_email.startsWith("system@")) {
    author = "TRUS";
  } else if (e.author_first_name && e.author_last_name) {
    author = `${e.author_first_name} ${e.author_last_name}`;
  } else {
    author = e.author_email;
  }

  return author;
};

interface Props {
  appId: number;
  event: TRUSEventShape;
  expanded: boolean;
  highlightNotes: boolean;
  isPinned?: boolean;
  onClick: any;
}

class TRUSEvent extends React.Component<Props, any> {
  descText = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      mounted: false,
      overflowed: null,
    };

    this.descText = React.createRef();
    this.clearDescTextOverflowedFlagToInitiateRetest = debounce(
      this.clearDescTextOverflowedFlagToInitiateRetest,
      250,
    );
  }

  componentDidMount() {
    window.addEventListener("resize", this.clearDescTextOverflowedFlagToInitiateRetest);

    this.setState(prevState => ({
      ...prevState,
      mounted: true,
    }));
  }

  componentDidUpdate() {
    const { current: elem } = this.descText;

    if (this.state.overflowed === null && this.descText.current) {
      this.setState(prevState => ({
        ...prevState,
        overflowed: elem.clientWidth < elem.scrollWidth,
      }));
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.clearDescTextOverflowedFlagToInitiateRetest);
  }

  clearDescTextOverflowedFlagToInitiateRetest = () => {
    this.setState(prevState => ({
      ...prevState,
      overflowed: null,
    }));
  };

  handleClick = () => {
    const { onClick } = this.props;

    this.clearDescTextOverflowedFlagToInitiateRetest();
    onClick();
  };

  render() {
    const { appId, event, expanded, highlightNotes, isPinned } = this.props;
    const { overflowed } = this.state;

    const eventClasses = classnames({
      [styles.event]: true,
      [styles.engItem]: event.scope === "eng",
      [styles.hurdleNote]: highlightNotes && isHurdleNoteKind(event.kind),
      [styles.pinned]: isPinned,
    });
    const descriptionClasses = classnames({
      [styles.description]: true,
      [styles.collapsed]: !expanded,
    });

    let showToggle: boolean;
    if (!this.descText.current) {
      showToggle = false;
    } else if (eventDoesNotHaveExpandedContent(event.kind)) {
      showToggle = false; // toggle not coherent since whole body always shown
    } else {
      showToggle = overflowed || expanded || event.fields;
    }
    const toggleClasses = classnames({
      [styles.toggle]: true,
      [styles.hidden]: !showToggle,
    });

    return (
      <li className={eventClasses}>
        <div className={styles.timestamp}>
          {formatTime(event.created_at)}
          <br />
          <span>{`by ${eventAuthor(event)}`}</span>
        </div>
        <div className={styles.title}>
          {kindToIcon(event.kind, !!isPinned)}
          <Title appId={appId} event={event} isPinned={!!isPinned} />
        </div>
        <div className={descriptionClasses} ref={this.descText}>
          <Description event={event} expanded={expanded} />
        </div>
        <button type="button" className={toggleClasses} onClick={this.handleClick}>
          <FontAwesome
            name={expanded ? "chevron-circle-down" : "chevron-circle-right"}
            fixedWidth
          />
        </button>
      </li>
    );
  }
}

export default TRUSEvent;
